/**
 * Subscriptions on a push-notification destination.
 *
 * A route is `(kind, source_id, min_severity)` — one kind, and up to two ways of
 * narrowing it. Both optional, neither subsuming the other:
 *
 * - `source_id` is equality against a producer's id, for a kind whose sources are a registry you
 *   can name before one fires. Absent means the wildcard — every source of that kind, including
 *   ones added later, which is why it is its own state rather than "all the boxes happen to be
 *   ticked".
 * - `min_severity` is an ordered floor, read like a log level, for a kind whose records differ in
 *   how much they matter. Trace review has no source registry at all: a report is an occurrence,
 *   not a definition.
 *
 * Triaged trace review findings offer neither: the tenant's live escalation standards are their narrowing.
 *
 * Which narrowings a kind offers is data, not a branch — see `KINDS`. The server
 * refuses one a kind does not offer, so the UI hides that control.
 *
 * `source_id` is a protobuf `bytes` field, so over the gateway it is base64.
 * Ids are compared as those strings and never decoded here.
 */

export const KIND_SIGNAL_DETECTION = "PUSH_NOTIFICATION_KIND_SIGNAL_DETECTION";
export const KIND_TRACE_REVIEW = "PUSH_NOTIFICATION_KIND_TRACE_REVIEW";
export const KIND_TRIAGED_TRACE_REVIEW = "PUSH_NOTIFICATION_KIND_TRIAGED_TRACE_REVIEW";

/**
 * The severity ladder, ascending. Mirrors `PushNotificationSeverity` in the
 * proto and the `min_severity` CHECK; the three move together. `UNSPECIFIED` is
 * not a rung — it is the absence of a floor.
 */
export const SEVERITY_UNSPECIFIED = "PUSH_NOTIFICATION_SEVERITY_UNSPECIFIED";

export const SEVERITY_RUNGS = [
    { wire: "PUSH_NOTIFICATION_SEVERITY_LOW", label: "Low" },
    { wire: "PUSH_NOTIFICATION_SEVERITY_MEDIUM", label: "Medium" },
    { wire: "PUSH_NOTIFICATION_SEVERITY_HIGH", label: "High" },
    { wire: "PUSH_NOTIFICATION_SEVERITY_CRITICAL", label: "Critical" },
];

/**
 * What each notification kind is, and how it can be narrowed.
 *
 * A table rather than a branch per kind, so the next input declaring a severity
 * inherits the control. `noun` is what a wildcard reads as in prose.
 */
export const KINDS = {
    // Insertion order IS the picker order: the rarest, most-judged alert first,
    // signals last.
    [KIND_TRIAGED_TRACE_REVIEW]: {
        label: "Triaged trace review findings",
        noun: "triaged trace review findings",
        hasSources: false,
        hasSeverity: false,
    },
    [KIND_TRACE_REVIEW]: {
        label: "Trace review findings",
        noun: "trace review findings",
        hasSources: false,
        hasSeverity: true,
        // Low is a firehose on a producer that runs every few turns; critical
        // hides most of what a reviewer wants.
        defaultSeverity: "PUSH_NOTIFICATION_SEVERITY_MEDIUM",
    },
    [KIND_SIGNAL_DETECTION]: {
        label: "Signal detections",
        noun: "detections",
        hasSources: true,
        hasSeverity: false,
    },
};

/** The kind a route names, defaulting to detections for a route that omits it. */
export function kindOf(route) {
    return route?.kind || KIND_SIGNAL_DETECTION;
}

/** Description of a kind, or `null` for one this build does not know. */
export function kindInfo(kind) {
    return KINDS[kind] ?? null;
}

/** The severity floor on a route, or "" when it carries none. */
export function severityOf(route) {
    const raw = route?.min_severity ?? route?.minSeverity ?? "";
    if (typeof raw !== "string" || raw === SEVERITY_UNSPECIFIED) return "";
    return raw;
}

/** The reader-facing label for a stored rung, or "" for no floor. */
export function severityLabel(wire) {
    return SEVERITY_RUNGS.find((rung) => rung.wire === wire)?.label ?? "";
}

/**
 * A route array subscribing to every source of a kind. `minSeverity` rides only
 * when the kind offers a floor; the server refuses one that does not.
 */
export function wildcardRoutes(kind = KIND_SIGNAL_DETECTION, minSeverity = "") {
    const route = { kind };
    if (minSeverity && kindInfo(kind)?.hasSeverity) route.min_severity = minSeverity;
    return [route];
}

/** A route array subscribing to specific sources. */
export function specificRoutes(sourceIds, kind = KIND_SIGNAL_DETECTION) {
    return sourceIds.map((id) => ({ kind, source_id: id }));
}

/** True when `routes` subscribes to every source of its kind. */
export function isWildcard(routes) {
    if (!Array.isArray(routes) || routes.length === 0) return false;
    return routes.some((r) => !sourceIdOf(r));
}

/** The base64 source id on a route, or "" for the wildcard. */
export function sourceIdOf(route) {
    const raw = route?.source_id ?? route?.sourceId ?? "";
    return typeof raw === "string" ? raw : "";
}

/**
 * Human-readable summary of what a destination is subscribed to.
 *
 * `namesById` maps base64 source id to display name. An id with no entry is
 * counted rather than named: a signal can be removed from the catalog while a
 * route still references it, and "1 other" is honest where a raw id would be
 * noise.
 */
export function describeRoutes(routes, namesById) {
    if (!Array.isArray(routes) || routes.length === 0) return "nothing";

    // One destination can subscribe to several kinds.
    const kinds = [...new Set(routes.map(kindOf))];
    if (kinds.length > 1) {
        return kinds
            .map((kind) => describeRoutes(routes.filter((r) => kindOf(r) === kind), namesById))
            .join("; ");
    }

    const [kind] = kinds;
    // An unknown kind means an api-server rolled ahead of the dashboard; say so
    // plainly rather than calling it detections.
    const noun = kindInfo(kind)?.noun ?? "notifications";

    if (isWildcard(routes)) {
        const floor = severityLabel(severityOf(routes.find((r) => severityOf(r)) ?? {}));
        // The same log-level shorthand the picker uses.
        return floor ? `${noun}, ${floor.toLowerCase()}+` : `all ${noun}`;
    }

    const names = [];
    let unknown = 0;
    for (const route of routes) {
        const name = namesById?.[sourceIdOf(route)];
        if (name) names.push(name);
        else unknown += 1;
    }
    names.sort((a, b) => a.localeCompare(b));

    const parts = [];
    if (names.length > 0) parts.push(names.slice(0, 3).join(", "));
    const overflow = Math.max(0, names.length - 3);
    const trailing = overflow + unknown;
    if (trailing > 0) parts.push(`${names.length > 3 ? "+" : ""}${trailing} other${trailing === 1 ? "" : "s"}`);
    return parts.join(" ") || "nothing";
}
